/**
 * Неудачный обычный лайк (issue #1260, решение продукта 23.10).
 *
 * - Любая ошибка `POST /api/likes` — один и тот же тост во всех точках лайка.
 * - В ленте лайк оптимистичный (карточка улетает сразу), поэтому при ошибке
 *   карточка возвращается наверх колоды, а неудачи считаются по анкете: на
 *   LIKE_FAILURES_BEFORE_SKIP-й неудаче подряд карточка больше не возвращается —
 *   лента остаётся на следующей анкете. Успешный лайк сбрасывает счётчик.
 * - Анкета, пропущенная из-за ошибки, НЕ пропуск (`/api/passes` навсегда скрыл бы
 *   её): она скрыта только до конца сессии — sessionStorage, ключ по id своего
 *   пользователя, стирается при выходе (tokenStorage.clearTokens). В следующей
 *   сессии сервер отдаёт её снова: лайка и пропуска на сервере нет.
 *
 * Чистые функции без React — юнит-тесты проекта идут без DOM.
 */

export const LIKE_FAILED_TOAST = "Произошла ошибка при попытке поставить лайк";

/** На какой неудаче подряд по одной анкете лента переходит к следующей. */
export const LIKE_FAILURES_BEFORE_SKIP = 5;

/* ──────── Счётчик неудач по анкете ──────── */

export type LikeFailureCounter = {
    /** Неудача лайка анкеты; возвращает, сколько неудач подряд у неё теперь. */
    fail: (profileId: string) => number;
    /** Лайк прошёл — счётчик анкеты обнуляется. */
    succeed: (profileId: string) => void;
    attempts: (profileId: string) => number;
};

export function createLikeFailureCounter(): LikeFailureCounter {
    const counts = new Map<string, number>();
    return {
        fail: (profileId) => {
            const next = (counts.get(profileId) ?? 0) + 1;
            counts.set(profileId, next);
            return next;
        },
        succeed: (profileId) => {
            counts.delete(profileId);
        },
        attempts: (profileId) => counts.get(profileId) ?? 0,
    };
}

/**
 * Что значит неудача `POST /api/likes`:
 *  - `ambiguous` — ответа нет (сеть, таймаут 20 с) или 2xx без данных: сервер мог
 *    лайк уже сохранить. Перед возвратом карточки сверяемся с сервером — иначе
 *    человек «пропустит» того, кого на самом деле лайкнул (у другого — лайк/мэтч);
 *  - `final` — повтор бессмыслен: 404 (анкета удалена или блокировка,
 *    `social_routes._not_found`) и 403 PROFILE_INCOMPLETE (своя анкета не заполнена) —
 *    тост и сразу следующая анкета, без 5 попыток;
 *  - `retryable` — остальное (5xx, 429 rate limit, 409, 400…): обычный путь с 5 попытками.
 */
export type LikeFailureKind = "ambiguous" | "final" | "retryable";

export type LikeResultLike = {
    data?: unknown;
    error?: string | null;
    code?: string | null;
    status?: number;
};

export function classifyLikeFailure(result: LikeResultLike): LikeFailureKind {
    const status = result.status;
    if (status === undefined || (status >= 200 && status < 300)) return "ambiguous";
    if (status === 404) return "final";
    if (status === 403 && (result.code === "PROFILE_INCOMPLETE" || result.error?.trim() === "PROFILE_INCOMPLETE")) return "final";
    return "retryable";
}

export type FeedLikeFailureOutcome = "confirmed" | "restored" | "skipped";

export type FeedLikeFailureDeps = {
    counter: LikeFailureCounter;
    toastError: (message: string) => void;
    /** Вернуть карточку наверх колоды (откат оптимистичного свайпа). */
    restoreCard: () => void;
    /** Оставить ленту на следующей анкете, а эту скрыть до конца сессии. */
    skipForSession: () => void;
    /**
     * Есть ли мой лайк этой анкеты на сервере (GET /api/likes/sent).
     * null — проверить не удалось.
     */
    likeExists: (profileId: string) => Promise<boolean | null>;
    /** Сервер лайк сохранил, хотя ответ потерялся — как обычный успех. */
    onLikeConfirmed: () => void;
};

/**
 * Лайк анкеты в ленте не удался.
 *  - Неоднозначная ошибка: сначала сверка с сервером; лайк есть — это успех
 *    (без тоста, карточка не возвращается, запись в «Вернуть» остаётся).
 *    Сверка не удалась или лайка нет — дальше как обычная ошибка.
 *  - Окончательная (404, 403 PROFILE_INCOMPLETE): тост и сразу следующая анкета.
 *  - Иначе тост; карточка возвращается, пока неудач меньше
 *    LIKE_FAILURES_BEFORE_SKIP, на N-й — анкета скрыта до конца сессии.
 * После перехода счётчик обнуляется: в следующий раз снова 5 попыток.
 */
export async function resolveFeedLikeFailure(
    profileId: string,
    result: LikeResultLike,
    deps: FeedLikeFailureDeps,
): Promise<FeedLikeFailureOutcome> {
    const kind = classifyLikeFailure(result);
    if (kind === "ambiguous") {
        let exists: boolean | null = null;
        try {
            exists = await deps.likeExists(profileId);
        } catch {
            exists = null;
        }
        if (exists === true) {
            deps.counter.succeed(profileId);
            deps.onLikeConfirmed();
            return "confirmed";
        }
    }
    deps.toastError(LIKE_FAILED_TOAST);
    const attempts = kind === "final" ? LIKE_FAILURES_BEFORE_SKIP : deps.counter.fail(profileId);
    if (attempts >= LIKE_FAILURES_BEFORE_SKIP) {
        deps.counter.succeed(profileId);
        deps.skipForSession();
        return "skipped";
    }
    deps.restoreCard();
    return "restored";
}

/** Сверка «лайк уже есть?» по списку моих лайков (сервер отдаёт свежие первыми). */
export function likeListContains(
    result: {data: readonly {profile: {id: string | number}}[] | null; error?: string | null},
    profileId: string,
): boolean | null {
    if (result.error || !Array.isArray(result.data)) return null;
    return result.data.some((entry) => String(entry.profile?.id) === profileId);
}

/* ──────── Колода ──────── */

/**
 * Поставить анкету текущей карточкой колоды, НЕ меняя индекс текущей карточки:
 * вставка на место `currentIndex`. Копии впереди (индекс ≥ текущего) убираются;
 * пролистанная копия позади остаётся «следом» — её не видно, а индекс не
 * сдвигается. Поэтому функция годится для `setProfiles(prev => …)`: два отката
 * подряд в одном батче (или откат + догрузка) складываются, а не затирают друг
 * друга, и `currentIndex` трогать не нужно.
 */
export function restoreCardOnTop<T extends {id: string | number}>(
    profiles: readonly T[],
    currentIndex: number,
    profile: T,
): T[] {
    const id = String(profile.id);
    if (profiles[currentIndex] && String(profiles[currentIndex].id) === id) return [...profiles];
    const head = profiles.slice(0, currentIndex);
    const tail = profiles.slice(currentIndex).filter((item) => String(item.id) !== id);
    return [...head, profile, ...tail];
}

/** Убрать из истории «Вернуть» последнюю запись обычного лайка анкеты. */
export function dropFailedLike<E extends {liked: boolean; superLike?: boolean; profile: {id: string | number}}>(
    history: readonly E[],
    profileId: string,
): E[] {
    for (let at = history.length - 1; at >= 0; at--) {
        const entry = history[at];
        if (entry.liked && !entry.superLike && String(entry.profile.id) === profileId) {
            return [...history.slice(0, at), ...history.slice(at + 1)];
        }
    }
    return [...history];
}

/**
 * Куда вернуть индекс по «Вернуть». После отката неудачного лайка в колоде может
 * быть «след» той же анкеты позади (restoreCardOnTop), поэтому первое вхождение
 * не годится. Порядок: позиция действия (`indexAtAction`), если там эта анкета;
 * иначе последнее вхождение до текущего индекса; иначе любое; иначе -1.
 */
export function undoTargetIndex<T extends {id: string | number}>(
    profiles: readonly T[],
    currentIndex: number,
    entry: {profile: {id: string | number}; indexAtAction: number},
): number {
    const id = String(entry.profile.id);
    const atAction = profiles[entry.indexAtAction];
    if (atAction && String(atAction.id) === id) return entry.indexAtAction;
    for (let i = Math.min(currentIndex, profiles.length) - 1; i >= 0; i--) {
        if (String(profiles[i].id) === id) return i;
    }
    return profiles.findIndex((item) => String(item.id) === id);
}

/**
 * Убрать анкету из колоды целиком (блокировка): все копии, включая следы.
 * `removedBehind` — сколько копий было позади текущего индекса: на столько
 * индекс и сдвигается, чтобы на экране осталась та же следующая анкета.
 */
export function removeFromDeck<T extends {id: string | number}>(
    profiles: readonly T[],
    currentIndex: number,
    profileId: string,
): {profiles: T[]; removedBehind: number; removed: number} {
    let removedBehind = 0;
    let removed = 0;
    const next = profiles.filter((item, index) => {
        if (String(item.id) !== profileId) return true;
        removed += 1;
        if (index < currentIndex) removedBehind += 1;
        return false;
    });
    return {profiles: next, removedBehind, removed};
}

/**
 * После «Вернуть»: восстановленная карточка встаёт текущей на `restoreIndex`, а
 * всё, что дальше, чистится — следы откатов (повторы анкет) и анкеты, скрытые до
 * конца сессии, иначе при отходе индекса назад они оказались бы впереди и
 * вернулись бы на экран (скрытая анкета — по разу на каждый след). Сама
 * восстановленная карточка не трогается. Голова колоды не меняется — у всех
 * оставшихся записей истории `indexAtAction` < `restoreIndex`.
 */
export function cleanTailAfterUndo<T extends {id: string | number}>(
    profiles: readonly T[],
    restoreIndex: number,
    sessionSkips: ReadonlySet<string>,
): T[] {
    const card = profiles[restoreIndex];
    if (!card) return [...profiles];
    const seen = new Set<string>([String(card.id)]);
    const tail: T[] = [];
    for (const item of profiles.slice(restoreIndex + 1)) {
        const id = String(item.id);
        if (seen.has(id) || sessionSkips.has(id)) continue;
        seen.add(id);
        tail.push(item);
    }
    return [...profiles.slice(0, restoreIndex), card, ...tail];
}

/** Видимое окно колоды без повторов анкет (следы отката не дают дублей ключей). */
export function uniqueWindow<T extends {id: string | number}>(profiles: readonly T[], from: number, size: number): T[] {
    const seen = new Set<string>();
    const out: T[] = [];
    for (let i = Math.max(0, from); i < profiles.length && out.length < size; i++) {
        const id = String(profiles[i].id);
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(profiles[i]);
    }
    return out;
}

/**
 * Пропуск на сессию, когда анкета снова текущая (её успели вернуть «Вернуть»):
 * убираем её с места текущей — следующая встаёт на это место, индекс не меняется.
 * Годится для `setProfiles(prev => …)`. Иначе (карточка уже улетела) — без изменений.
 */
export function dropCurrentIfSkipped<T extends {id: string | number}>(
    profiles: readonly T[],
    currentIndex: number,
    profileId: string,
): T[] {
    const current = profiles[currentIndex];
    if (!current || String(current.id) !== profileId) return [...profiles];
    return [...profiles.slice(0, currentIndex), ...profiles.slice(currentIndex + 1)];
}

/** Анкеты без скрытых до конца сессии. */
export function withoutSessionSkips<T extends {id: string | number}>(profiles: readonly T[], skipped: ReadonlySet<string>): T[] {
    if (skipped.size === 0) return [...profiles];
    return profiles.filter((profile) => !skipped.has(String(profile.id)));
}

/* ──────── Пропущенные из-за ошибки: только эта сессия ──────── */

export const LIKE_ERROR_SKIPS_PREFIX = "rl.likeErrorSkips:";

type SkipStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type EnumerableStorage = SkipStorage & Pick<Storage, "key" | "length">;

function defaultStorage(): EnumerableStorage | null {
    try {
        return typeof sessionStorage === "undefined" ? null : sessionStorage;
    } catch {
        return null;
    }
}

function skipsKey(ownerId: string): string {
    return `${LIKE_ERROR_SKIPS_PREFIX}${ownerId}`;
}

/** `ownerId` — id своего пользователя; без него ничего не читается и не пишется. */
export function readLikeErrorSkips(
    ownerId: string | number | null | undefined,
    storage: SkipStorage | null = defaultStorage(),
): Set<string> {
    if (ownerId == null || ownerId === "" || !storage) return new Set();
    try {
        const parsed: unknown = JSON.parse(storage.getItem(skipsKey(String(ownerId))) || "[]");
        return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
        return new Set();
    }
}

export function addLikeErrorSkip(
    ownerId: string | number | null | undefined,
    profileId: string,
    storage: SkipStorage | null = defaultStorage(),
): void {
    if (ownerId == null || ownerId === "" || !storage) return;
    const skipped = readLikeErrorSkips(ownerId, storage);
    skipped.add(profileId);
    try {
        storage.setItem(skipsKey(String(ownerId)), JSON.stringify([...skipped]));
    } catch {
        // Квота/приватный режим: анкета останется скрытой до перезагрузки (в памяти ленты).
    }
}

/** Выход из аккаунта: стереть пропуски из-за ошибки всех аккаунтов этой вкладки. */
export function clearLikeErrorSkipsOnLogout(storage: EnumerableStorage | null = defaultStorage()): void {
    if (!storage) return;
    try {
        const keys: string[] = [];
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key && key.startsWith(LIKE_ERROR_SKIPS_PREFIX)) keys.push(key);
        }
        keys.forEach((key) => storage.removeItem(key));
    } catch {
        /* хранилище недоступно — нечего чистить */
    }
}
